import { useEffect } from 'react';

import { parseCapture } from '@flow/capture-parser';
import {
  captureItem,
  completeBlock,
  completeTask,
  createBlock,
  reopenBlock,
  reopenTask,
  setBlockTags,
  sortKeyAfter,
  trashBlock,
  triageItem,
  updateBlockBody,
  updateBlockDates,
  updateBlockTitle,
} from '@flow/domain';
import { applyLocalOperations, queries, search } from '@flow/sync';

import type { LocalDatabase } from './powersync.js';

/**
 * Handles for the perf tooling (apps/web/perf): the open database plus the domain commands and the
 * local executor, so probes write exactly as the screens do (f1-local-write-latency). No UI uses them.
 */
export interface DevHandles {
  db: LocalDatabase['db'];
  userId: string;
  vfs: string;
  commands: {
    createBlock: typeof createBlock;
    captureItem: typeof captureItem;
    completeTask: typeof completeTask;
    reopenTask: typeof reopenTask;
    triageItem: typeof triageItem;
    parseCapture: typeof parseCapture;
    completeBlock: typeof completeBlock;
    reopenBlock: typeof reopenBlock;
    trashBlock: typeof trashBlock;
    updateBlockTitle: typeof updateBlockTitle;
    updateBlockDates: typeof updateBlockDates;
    updateBlockBody: typeof updateBlockBody;
    setBlockTags: typeof setBlockTags;
    sortKeyAfter: typeof sortKeyAfter;
  };
  applyLocalOperations: typeof applyLocalOperations;
  queries: typeof queries;
  /** The local full-text search, as the palette and the search page call it (f1-trash-search). */
  search: typeof search;
  /** Reconnects the sync stream with the app's connector (after `db.disconnect()`). */
  connect: () => Promise<void>;
}

declare global {
  interface Window {
    __flowDev?: DevHandles;
    /** Arguments of the browser script run by apps/web/perf/probe-latency.ts. */
    __flowProbeArgs?: Record<string, unknown>;
  }
}

export function useDevHandles(local: LocalDatabase, userId: string): void {
  useEffect(() => {
    window.__flowDev = {
      db: local.db,
      userId,
      vfs: local.vfs.name,
      commands: {
        createBlock,
        captureItem,
        completeTask,
        reopenTask,
        triageItem,
        parseCapture,
        completeBlock,
        reopenBlock,
        trashBlock,
        updateBlockTitle,
        updateBlockDates,
        updateBlockBody,
        setBlockTags,
        sortKeyAfter,
      },
      applyLocalOperations,
      queries,
      search,
      connect: () => local.db.connect(local.connector),
    };
    return () => {
      delete window.__flowDev;
    };
  }, [local, userId]);
}
